// NextStepSuggestions — lightweight, zero-cost "what next?" chips that
// appear under each completed assistant reply in the chat shell.
//
// Why heuristic (not another AI call): we want this strip to feel
// instant, free, and predictable. It exists to nudge the user toward
// alternate framings, deeper dives, contrarian takes, and creative
// twists — *especially* when they're inside a playbook and might
// otherwise march straight through the canonical path. Spending a
// gateway call per turn just to suggest "ask for the opposite view"
// would be wasteful.
//
// What renders
//   1. 3–4 follow-up prompt chips (one click → drops into composer).
//      If a playbook is active and there's a *next* step the user
//      hasn't run, we surface it as the first chip. Otherwise we
//      generate generic "alternate ending / creative twist / steelman /
//      go deeper" prompts so every assistant turn has something to
//      explore.
//   2. One "try with <other model>" chip — picks a model from a
//      *different family* than the current brain, biased toward a
//      complementary strength (e.g. heavy-reasoner if you're on a
//      flash, fast/cheap if you're on a heavy model). Click swaps the
//      composer model so the user can re-ask the same question with a
//      different brain.
import { Lightbulb, Sparkles, Wand2, Drama, ArrowRight, Cpu } from "lucide-react";
import { AVAILABLE_MODELS, modelLabel, findModel } from "@/lib/llmModels";

type Suggestion = {
  label: string;
  prompt: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Visible hint about *why* this nudges you somewhere new. */
  hint?: string;
};

interface Props {
  /** Current chat brain id (e.g. `google/gemini-3-flash-preview`). */
  currentModel: string;
  /** Active playbook step prompt the user *hasn't* hit yet, if any. */
  nextPlaybookStep?: { stepNumber: number; title: string; prompt: string } | null;
  /** Drops a prompt into the composer (without sending). */
  onPickPrompt: (prompt: string) => void;
  /** Switches the chat brain to the given model id. */
  onPickModel: (modelId: string) => void;
}

const GENERIC_SUGGESTIONS: Suggestion[] = [
  {
    label: "Alternate ending",
    prompt:
      "Reframe your last answer with a completely different ending or outcome. What would change if the core assumption were inverted?",
    icon: Drama,
    hint: "Flip the assumption",
  },
  {
    label: "Creative twist",
    prompt:
      "Give me a more creative, unconventional take on what you just said — surprise me. Use a metaphor or analogy I wouldn't expect.",
    icon: Wand2,
    hint: "Less safe, more interesting",
  },
  {
    label: "Steelman the opposite",
    prompt:
      "Now argue the strongest possible case *against* your previous answer. Be specific — what would a thoughtful critic say?",
    icon: Lightbulb,
    hint: "Pressure-test it",
  },
  {
    label: "Go one level deeper",
    prompt:
      "Pick the single most interesting claim you just made and go one level deeper — explain the mechanism, the trade-offs, and one concrete example.",
    icon: Sparkles,
    hint: "Drill into the best part",
  },
];

/**
 * Recommend a complementary model: prefer a different family + a
 * different strength tier (cheap ↔ heavy-reasoner). Falls back to any
 * other model if the registry shrinks in the future.
 */
function recommendModel(currentId: string): { id: string; reason: string } | null {
  const current = findModel(currentId);
  if (!current) return null;
  const otherFamily = AVAILABLE_MODELS.filter((m) => m.family !== current.family);
  const pool = otherFamily.length > 0 ? otherFamily : AVAILABLE_MODELS.filter((m) => m.id !== currentId);
  if (pool.length === 0) return null;
  // If user is on a non-premium brain, suggest a premium one for "deeper".
  // If they're on premium, suggest a cheap/fast one for "what would a
  // lighter model say" — both directions feel like real alternatives.
  const target = current.premium
    ? pool.find((m) => !m.premium)
    : pool.find((m) => m.premium);
  const pick = target ?? pool[0];
  const reason = current.premium
    ? "Lighter brain — quick second opinion"
    : "Heavier brain — deeper reasoning";
  return { id: pick.id, reason };
}

const NextStepSuggestions = ({
  currentModel,
  nextPlaybookStep,
  onPickPrompt,
  onPickModel,
}: Props) => {
  // Build the 3–4 prompt chips. Playbook step (if any) takes the first slot.
  const promptSuggestions: Suggestion[] = [];
  if (nextPlaybookStep) {
    promptSuggestions.push({
      label: `Step ${nextPlaybookStep.stepNumber} · ${nextPlaybookStep.title}`,
      prompt: nextPlaybookStep.prompt,
      icon: ArrowRight,
      hint: "Continue the playbook",
    });
    promptSuggestions.push(...GENERIC_SUGGESTIONS.slice(0, 3));
  } else {
    promptSuggestions.push(...GENERIC_SUGGESTIONS);
  }

  const modelRec = recommendModel(currentModel);

  return (
    <div
      className="mt-2 pt-2 border-t border-border/40 space-y-1.5"
      aria-label="Next step suggestions"
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        Try next
      </div>
      <div className="flex flex-wrap gap-1.5">
        {promptSuggestions.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.label}
              type="button"
              onClick={() => onPickPrompt(s.prompt)}
              className="group inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1 text-[11px] font-mono text-foreground hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors max-w-full"
              title={`${s.hint ?? "Use this prompt"} — drops into composer, edit before sending`}
            >
              <Icon className="w-3 h-3 shrink-0" />
              <span className="truncate">{s.label}</span>
            </button>
          );
        })}
        {modelRec && (
          <button
            type="button"
            onClick={() => onPickModel(modelRec.id)}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-2 py-1 text-[11px] font-mono text-primary hover:bg-primary/10 hover:border-primary transition-colors"
            title={`${modelRec.reason} — switches the brain for your next message`}
          >
            <Cpu className="w-3 h-3" />
            Try with {modelLabel(modelRec.id)}
          </button>
        )}
      </div>
    </div>
  );
};

export default NextStepSuggestions;